"use client";

import { useEffect } from "react";
import posthog from "posthog-js";
import { Button } from "@/components/ui/Button";
import { StatusCard } from "@/components/ui/StatusCard";
import { reloadOnceForChunkError } from "@/lib/chunks/chunkErrors";
import "./globals.css";

export default function GlobalError({
  error,
  unstable_retry,
}: {
  error: Error & { digest?: string };
  unstable_retry: () => void;
}) {
  useEffect(() => {
    console.error(error);
    // React error boundaries catch render errors before they reach window.onerror, so
    // capture_exceptions autocapture (PostHogInit) does not see them.
    // Explicit captureException is required here. Import posthog directly — global-error.tsx
    // renders its own <html> outside the PostHogProvider tree.
    posthog.captureException(error);
    // unstable_retry() re-renders against the same stale chunk URL and can never
    // recover a ChunkLoadError — only a fresh navigation re-fetches the
    // build manifest. Deferred + cooldown-guarded; falls through to the card below
    // within the cooldown window (see reloadOnceForChunkError's own doc comment).
    reloadOnceForChunkError(error);
  }, [error]);

  return (
    <html lang="en">
      <body className="min-h-screen flex items-center justify-center bg-surface text-text px-6 py-14">
        <div className="max-w-[1180px] mx-auto w-full">
          <StatusCard
            tone="error"
            title="Something went wrong"
            body="The app hit an unexpected error. Please try again."
            actions={<Button onClick={() => unstable_retry()}>Try again</Button>}
          />
        </div>
      </body>
    </html>
  );
}
